import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, RefreshCw, Search } from 'lucide-react';
import { Aviso } from '@/components/ui/aviso';
import { Button } from '@/components/ui/button';
import { CeldaProducto } from '@/components/ui/celda-producto';
import type { TonoDeCifra } from '@/components/ui/cifras';
import { cn } from '@/lib/utils/cn';
import { Tarjeta as TarjetaDeCompite } from '@/features/compite/ui/tarjeta';
import { EstadoError, EstadoVacio } from '@/components/ui/estado-vacio';
import { Etiqueta, type TonoDeEtiqueta } from '@/components/ui/etiqueta';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SelectNativo } from '@/components/ui/select-nativo';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { strategyApplicationsApi } from '@/features/smart-pricing/services/strategy-applications-api';
import { formatoCantidad, haceCuanto } from '@/lib/utils/formato-precio';
import { hrefDeLaEmpresa } from '@/router/href-de-la-empresa';
import { hrefsDeCompite } from '@/features/compite/lib/rutas';
import { BarraDeRangos, formatoPrecio, pisoEnTexto, techoEnTexto } from './barra-de-rangos';
import { DesgloseSimulado } from './desglose-simulado';
import { pricingRulesApi } from '../services/pricing-rules-api';
import type {
  Cruce,
  EstadoSimulado,
  FilaDeLaFoto,
  FiltroFoto,
  Foto,
  PricingRule,
} from '../types/pricing-rules.types';
import { describirAlcance, estadoDeRegla } from '../lib/reglas-en-palabras';
import { agruparMarcas } from '../lib/marcas';
import {
  TEXTO_ESTADO,
  nombresDeLimitantes,
  porQueDeLaFila,
  textoDeMargenPropio,
} from '../lib/fila-en-palabras';
import { ESPERA_AL_TIPEAR_MS } from '../lib/use-pedido-con-espera';

/**
 * «Cómo está cada producto» (maqueta S10; pulido, spec 232). Lee la FOTO del
 * catálogo (4B-datos T8): la corrida entera, calculada de antemano. Responde al
 * instante, dice «calculado hace N min», y mientras se recalcula muestra lo que
 * hay con «recalculando…» y vuelve a pedir cada 3 s. El detalle de una fila sale
 * de la misma fila: tocar un producto no llama a nadie.
 *
 * La foto puede estar atrasada: un cambio de competencia no la recalcula. Con
 * `error` deja de preguntar y lo dice; si pasan 2 min recalculando (sin worker,
 * o un cálculo que murió en el medio), lo dice y pregunta cada 30 s. «Actualizar
 * esta vista» nunca se deshabilita: es la salida de esos dos casos.
 *
 * Agregados del 29-sep (spec 232, A5): «¿Y si…?» ya no se monta acá. Probaba
 * contra una sola regla y, metido en Productos, parecía mostrar cómo quedan los
 * precios. Su código (`y-si.tsx`, `si-se-aplica.tsx`) queda para el Simulador,
 * que tendrá su propia maqueta; lo que pedía lo cubre «Resultados esperados»
 * al crear una estrategia.
 */

export interface ComoEstaCadaProductoProps {
  reglas: PricingRule[];
  filtroInicial?: { sinCosto?: boolean; estado?: EstadoSimulado } | null;
  /**
   * El destino Productos del plan 3 (P3-D2) lo pasa: la fila abierta ofrece el
   * historial de precios del producto. Ausente = no se ofrece.
   */
  onVerHistorial?: (p: { productId: string; name: string }) => void;
  /** Cambia cuando el precio de un producto cambió desde afuera (volver en el historial). */
  version?: number;
  /** Cada cuánto se vuelve a pedir mientras recalcula. Sólo lo cambian los tests. */
  esperaEntreConsultasMs?: number;
  /** Cada cuánto, cuando ya tarda más de lo normal. Sólo lo cambian los tests. */
  esperaSiTardaMs?: number;
}

/** Recalculando desde hace más que esto (desde `pedidaEn`) = «está tardando más de lo normal». */
const TARDA_DEMASIADO_MS = 2 * 60 * 1000;

const POR_PAGINA = 20;
const fmt = (n: number | null | undefined) => (n == null ? '—' : formatoPrecio(n));
const conMayuscula = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Las seis cifras de arriba (maqueta: «con una frase corta»). `ayuda` es la frase
 * larga de antes: queda en el `title`, para quien pasa el mouse. `tono`: sólo las
 * que piden atención llevan color, y sólo cuando hay alguno.
 */
type Tarjeta = {
  clave: string;
  texto: string;
  /** Con 1, si el rótulo cambia: «1 lo limita una regla» (auditoría M3). */
  textoDeUno?: string;
  ayuda: string;
  tono: TonoDeCifra;
  filtro: Pick<FiltroFoto, 'estado' | 'sinCosto'>;
};
const TARJETAS: Tarjeta[] = [
  {
    clave: 'LIBRE',
    texto: 'dentro del rango',
    ayuda: 'La estrategia decide sola: ninguna regla la limita.',
    tono: 'neutro',
    filtro: { estado: 'LIBRE' },
  },
  {
    clave: 'LIMITADO',
    texto: 'los limita una regla',
    textoDeUno: 'lo limita una regla',
    ayuda: 'Quedan en el borde de lo que dejan las reglas.',
    tono: 'alerta',
    filtro: { estado: 'LIMITADO' },
  },
  {
    clave: 'SIN_PRECIO_POSIBLE',
    texto: 'conflicto entre reglas',
    ayuda: 'Las reglas no se pueden cumplir todas: te esperan en Revisar precios.',
    tono: 'bloqueo',
    filtro: { estado: 'SIN_PRECIO_POSIBLE' },
  },
  {
    clave: 'sinCosto',
    texto: 'sin costo',
    ayuda: 'Sin costo, las reglas de margen no los cuidan.',
    tono: 'alerta',
    filtro: { sinCosto: true },
  },
  {
    clave: 'SIN_DATOS',
    // SIN_DATOS junta varias causas (sin competencia, la estrategia no decidió,
    // pidió un precio imposible y, desde el quinto método del plan 3, P3-D1, sin
    // precio de lista o sin costo): la cifra no nombra una; cada fila dice la suya.
    texto: 'no se pueden calcular',
    textoDeUno: 'no se puede calcular',
    ayuda: 'Les falta competencia, costo o precio de lista: cada fila dice la suya.',
    tono: 'neutro',
    filtro: { estado: 'SIN_DATOS' },
  },
  {
    clave: 'SIN_ESTRATEGIA',
    texto: 'sin estrategia',
    ayuda: 'Ninguna estrategia los alcanza: no se mueven solos.',
    tono: 'neutro',
    filtro: { estado: 'SIN_ESTRATEGIA' },
  },
];

/** El color de la cifra: sólo las que piden atención, y sólo cuando hay alguno. */
const TINTA_DE_LA_CIFRA: Record<TonoDeCifra, string> = {
  neutro: 'text-foreground',
  alerta: 'text-alerta',
  bloqueo: 'text-bloqueo',
  ok: 'text-ok',
  baja: 'text-baja',
};

/** El tono de «Cómo está» (maqueta: una etiqueta de tono en vez de las mayúsculas). */
const TONO_DEL_ESTADO: Record<EstadoSimulado, TonoDeEtiqueta> = {
  LIBRE: 'ok',
  LIMITADO: 'alerta',
  SIN_PRECIO_POSIBLE: 'bloqueo',
  SIN_DATOS: 'neutro',
  SIN_ESTRATEGIA: 'neutro',
};

function textoCruce(c: Cruce, hayLimites: boolean): string {
  if (c.vacio) return '—';
  if (!hayLimites || (c.min == null && c.max == null)) return 'sin límite';
  if (c.min != null && c.max != null) return `${pisoEnTexto(c.min)} – ${techoEnTexto(c.max)}`;
  return c.min != null ? `desde ${pisoEnTexto(c.min)}` : `hasta ${techoEnTexto(c.max!)}`;
}

export function ComoEstaCadaProducto({
  reglas,
  filtroInicial = null,
  onVerHistorial,
  version = 0,
  esperaEntreConsultasMs = 3000,
  esperaSiTardaMs = 30_000,
}: ComoEstaCadaProductoProps) {
  const [tarjeta, setTarjeta] = useState<Pick<FiltroFoto, 'estado' | 'sinCosto'>>(
    filtroInicial ?? {}
  );
  const [q, setQ] = useState('');
  const [qAplicada, setQAplicada] = useState('');
  const [category, setCategory] = useState('');
  const [brand, setBrand] = useState('');
  const [estrategiaId, setEstrategiaId] = useState('');
  const [reglaId, setReglaId] = useState('');
  const [reglaModo, setReglaModo] = useState<'ALCANZA' | 'LIMITA'>('LIMITA');
  const [pagina, setPagina] = useState(1);
  const [abierta, setAbierta] = useState<string | null>(null);

  const [foto, setFoto] = useState<Foto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vuelta, setVuelta] = useState(0);

  const [categorias, setCategorias] = useState<string[]>([]);
  const [marcas, setMarcas] = useState<string[]>([]);
  const [estrategias, setEstrategias] = useState<Array<{ id: string; name: string }>>([]);

  // Las listas de los filtros: si alguna falla, ese filtro queda con «Todas».
  useEffect(() => {
    pricingRulesApi
      .listCategories()
      .then((c) => setCategorias(c.map((x) => x.name)))
      .catch(() => {});
    pricingRulesApi
      .listBrands()
      .then((b) => setMarcas(agruparMarcas(b.brands).map((m) => m.name)))
      .catch(() => {});
    strategyApplicationsApi
      .listar()
      .then((l) => setEstrategias(l.map((e) => ({ id: e.id, name: e.name }))))
      .catch(() => {});
  }, []);

  // La búsqueda espera a que se deje de tipear.
  useEffect(() => {
    const t = setTimeout(() => {
      setQAplicada(q.trim());
      setPagina(1);
    }, ESPERA_AL_TIPEAR_MS);
    return () => clearTimeout(t);
  }, [q]);

  const filtro: FiltroFoto = useMemo(
    () => ({
      ...tarjeta,
      q: qAplicada || undefined,
      category: category || undefined,
      brand: brand || undefined,
      estrategiaId: estrategiaId || undefined,
      reglaId: reglaId || undefined,
      reglaModo: reglaId ? reglaModo : undefined,
      page: pagina,
      limit: POR_PAGINA,
    }),
    [tarjeta, qAplicada, category, brand, estrategiaId, reglaId, reglaModo, pagina]
  );

  const ultimo = useRef(0);
  const cargar = useCallback(async () => {
    const mio = ++ultimo.current;
    try {
      const f = await pricingRulesApi.foto(filtro);
      if (mio !== ultimo.current) return;
      setFoto(f);
      setError(null);
    } catch (e) {
      if (mio !== ultimo.current) return;
      setError(
        mensajeDeError(e, 'No se pudo leer cómo está cada producto. Los precios no cambiaron.')
      );
    }
  }, [filtro]);

  useEffect(() => {
    void cargar();
  }, [cargar, vuelta, version]);

  // Recalculando desde hace más de 2 min: sin worker, o un cálculo que murió en
  // el medio (el backend sólo vuelve a pedir sola la PRIMERA foto perdida).
  const tardando =
    !!foto?.recalculando &&
    !!foto.pedidaEn &&
    Date.now() - new Date(foto.pedidaEn).getTime() > TARDA_DEMASIADO_MS;

  // Mientras recalcula, se vuelve a pedir cada 3 s hasta que termine (cada 30 s
  // si ya tarda demasiado). Con `error`, `recalculando` viene en false: se deja
  // de preguntar hasta que alguien apriete «Actualizar esta vista».
  useEffect(() => {
    if (!foto?.recalculando) return;
    const t = setTimeout(
      () => setVuelta((v) => v + 1),
      tardando ? esperaSiTardaMs : esperaEntreConsultasMs
    );
    return () => clearTimeout(t);
  }, [foto, tardando, esperaEntreConsultasMs, esperaSiTardaMs]);

  const recalcular = async () => {
    try {
      await pricingRulesApi.recalcularFoto();
      setVuelta((v) => v + 1);
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudo pedir el recálculo. Probá de nuevo.'));
    }
  };

  const cambiarFiltro = (fn: () => void) => {
    fn();
    setPagina(1);
    setAbierta(null);
  };

  const alcancePorRegla = useMemo(
    () =>
      // Sin minúsculas: «Sony» es una marca (B6). La barra no lo repite si el nombre ya lo dice.
      Object.fromEntries(reglas.map((r) => [r.id, describirAlcance(r.scope ?? {})])),
    [reglas]
  );
  const reglasQueCorren = useMemo(() => reglas.filter((r) => estadoDeRegla(r).corre), [reglas]);

  // Sin foto calculada (la primera vez, o un error sin anterior) el backend manda
  // `conteos` en cero: no es «0 productos», es que no se contó. Ausente = «—».
  const conteoDe = (t: Tarjeta) =>
    foto?.calculadaEn
      ? t.clave === 'sinCosto'
        ? foto.conteos.sinCosto
        : foto.conteos[t.clave as EstadoSimulado]
      : null;
  const esActiva = (t: Tarjeta) =>
    (t.filtro.estado ?? null) === (tarjeta.estado ?? null) &&
    !!t.filtro.sinCosto === !!tarjeta.sinCosto;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 space-y-1">
          <h2 className="tracking-tight text-xl font-semibold text-foreground">
            Cómo está cada producto con tus reglas de hoy
          </h2>
          <p className="text-sm text-muted-foreground">
            Lo que haría la corrida con cada producto si corriera ahora. Mirar esto no mueve ningún
            precio.{' '}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="rounded-sm font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  ¿Cuándo se actualiza?
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-96 rounded-xl text-sm">
                Se calcula con los precios, costos y competencia del momento del cálculo. Se
                actualiza solo al cambiar reglas o estrategias, cuando una corrida aplica precios,
                al aprobar en Revisar precios, al cambiar a mano el precio, el costo, la marca o la
                categoría de un producto, al importar un archivo de precios y cada noche. Un cambio
                de competencia se ve con «Actualizar esta vista».
              </PopoverContent>
            </Popover>
          </p>
        </div>
        <div
          className="flex shrink-0 items-center gap-3 whitespace-nowrap text-xs text-muted-foreground"
          aria-live="polite"
        >
          {foto?.recalculando ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 animate-pulse rounded-full bg-info" aria-hidden="true" />
              {foto.calculadaEn ? 'recalculando…' : 'calculando por primera vez…'}
            </span>
          ) : foto?.calculadaEn ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-ok" aria-hidden="true" />
              calculado {haceCuanto(foto.calculadaEn)}
            </span>
          ) : foto ? (
            <span>todavía no se calculó</span>
          ) : null}
          {/* Nunca deshabilitado: es la salida de «tarda demasiado» y del error.
              Lote C1 (D11): «Actualizar esta vista», para no confundirlo con
              «Aplicar las reglas y recalcular» de Reglas, que sí propone precios. */}
          <Button
            size="sm"
            variant="outline"
            className="h-9 gap-1.5 rounded-full px-4"
            onClick={() => void recalcular()}
          >
            <RefreshCw
              className={cn('h-3.5 w-3.5', foto?.recalculando && 'animate-spin')}
              aria-hidden="true"
            />
            Actualizar esta vista
          </Button>
        </div>
      </div>

      {tardando && (
        <Aviso tono="alerta" className="rounded-tarjeta px-4 py-3 text-sm">
          Está tardando más de lo normal: puede que el cálculo no esté corriendo. Probá «Actualizar
          esta vista».
        </Aviso>
      )}

      {/* Con foto anterior, el aviso va arriba; sin ella, el error va en lugar de la tabla. */}
      {foto?.error && !foto.recalculando && foto.calculadaEn && (
        <Aviso
          tono="alerta"
          className="rounded-tarjeta px-4 py-3 text-sm"
        >{`${foto.error}: se ve la anterior. Probá «Actualizar esta vista».`}</Aviso>
      )}

      {/* Las seis cifras (maqueta Compite): cada una es una tarjeta que filtra la
          tabla; la elegida queda marcada con el borde y la raya de la marca. */}
      <div
        role="group"
        aria-label="Cómo están"
        className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6"
      >
        {TARJETAS.map((t) => {
          const n = conteoDe(t);
          // Lote C1 (punto 10): «sin costo · 12 con 0,00».
          const enCero = t.clave === 'sinCosto' && foto?.calculadaEn ? foto.conteos.costoEnCero : 0;
          const rotulo = n === 1 && t.textoDeUno ? t.textoDeUno : t.texto;
          const tono: TonoDeCifra = n != null && n > 0 ? t.tono : 'neutro';
          const activa = esActiva(t);
          return (
            <button
              key={t.clave}
              type="button"
              data-tono={tono}
              aria-pressed={activa}
              title={t.ayuda}
              onClick={() => cambiarFiltro(() => setTarjeta(activa ? {} : t.filtro))}
              className={cn(
                'relative min-w-0 overflow-hidden rounded-tarjeta border bg-card px-4 pb-4 pt-3.5 text-left shadow-sm transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                activa ? 'border-primary' : 'border-border hover:bg-muted/40'
              )}
            >
              <b
                className={cn(
                  'block text-3xl font-semibold tabular-nums leading-tight',
                  TINTA_DE_LA_CIFRA[tono]
                )}
              >
                {n == null ? '—' : formatoCantidad(n)}
              </b>{' '}
              {/* ^ el espacio es para el nombre accesible («4 los limita una regla»). */}
              <span className="mt-1 block text-sm leading-snug text-muted-foreground">
                {enCero ? `${rotulo} · ${formatoCantidad(enCero)} con 0,00` : rotulo}
              </span>
              {activa && (
                <span
                  aria-hidden="true"
                  className="bg-primary absolute inset-x-0 bottom-0 h-[3px]"
                />
              )}
            </button>
          );
        })}
      </div>

      <TarjetaDeCompite className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 px-5 py-3.5 text-sm">
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              aria-label="Buscar"
              placeholder="Buscar por nombre o código"
              className="h-9 w-64 rounded-xl bg-muted/40 pl-9"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <SelectNativo
            aria-label="Categoría"
            className="max-w-52 rounded-xl"
            value={category}
            onChange={(e) => cambiarFiltro(() => setCategory(e.target.value))}
          >
            <option value="">Categoría: todas</option>
            {categorias.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </SelectNativo>
          <SelectNativo
            aria-label="Marca"
            className="max-w-52 rounded-xl"
            value={brand}
            onChange={(e) => cambiarFiltro(() => setBrand(e.target.value))}
          >
            <option value="">Marca: todas</option>
            {marcas.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </SelectNativo>
          <SelectNativo
            aria-label="Estrategia"
            className="max-w-52 rounded-xl"
            value={estrategiaId}
            onChange={(e) => cambiarFiltro(() => setEstrategiaId(e.target.value))}
          >
            <option value="">Estrategia: todas</option>
            <option value="NINGUNA">Sin estrategia</option>
            {estrategias.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </SelectNativo>
          <SelectNativo
            aria-label="Regla"
            className="max-w-52 rounded-xl"
            value={reglaId}
            onChange={(e) => cambiarFiltro(() => setReglaId(e.target.value))}
          >
            <option value="">Regla: todas</option>
            {/* Sólo las que corren hoy: la foto no cuenta los alcanzados de una apagada,
                vencida o con condiciones viejas, y elegirla daría «Ningún producto». */}
            {reglasQueCorren.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </SelectNativo>
          {reglaId && (
            <SelectNativo
              aria-label="Qué hace la regla"
              className="rounded-xl"
              value={reglaModo}
              onChange={(e) =>
                cambiarFiltro(() => setReglaModo(e.target.value as 'ALCANZA' | 'LIMITA'))
              }
            >
              <option value="LIMITA">los que limita</option>
              <option value="ALCANZA">los que alcanza</option>
            </SelectNativo>
          )}
        </div>

        {error ? (
          <div className="border-t border-border px-5 py-4">
            <EstadoError onReintentar={() => setVuelta((v) => v + 1)} className="rounded-xl">
              {error}
            </EstadoError>
          </div>
        ) : !foto ? (
          <div
            className="space-y-2 border-t border-border px-5 py-4"
            role="status"
            aria-label="Cargando"
          >
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-xl" />
            ))}
          </div>
        ) : foto.calculadaEn == null && foto.filas.length === 0 ? (
          <div className="border-t border-border px-5 py-5">
            <EstadoVacio
              className="rounded-tarjeta"
              titulo={
                // Con el aviso de «tardando» arriba, no se promete «unos segundos».
                foto.recalculando
                  ? tardando
                    ? 'Calculando por primera vez…'
                    : 'Calculando por primera vez: tarda unos segundos.'
                  : foto.error
                    ? `No se pudo calcular cómo está cada producto (${foto.error}). Probá «Actualizar esta vista».`
                    : 'Todavía no se calculó cómo está cada producto. Apretá «Actualizar esta vista».'
              }
            />
          </div>
        ) : (
          <>
            <div className="border-t border-border">
              <Table className="min-w-[860px]">
                <TableHeader className="bg-muted/50">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-10 pl-5 text-xs font-semibold">
                      Producto · estrategia
                    </TableHead>
                    <TableHead className="h-10 text-right text-xs font-semibold">
                      La estrategia pide (Bs)
                    </TableHead>
                    <TableHead className="h-10 text-xs font-semibold">
                      Lo que dejan las reglas
                    </TableHead>
                    <TableHead className="h-10 text-right text-xs font-semibold">
                      Propone (Bs)
                    </TableHead>
                    <TableHead className="h-10 pr-5 text-xs font-semibold">Cómo está</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {foto.filas.length === 0 && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                        Ningún producto con estos filtros.
                      </TableCell>
                    </TableRow>
                  )}
                  {foto.filas.map((f) => (
                    <FilaDeProducto
                      key={f.productId}
                      f={f}
                      abierta={abierta === f.productId}
                      onTocar={() => setAbierta(abierta === f.productId ? null : f.productId)}
                      alcancePorRegla={alcancePorRegla}
                      onVerHistorial={onVerHistorial}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="border-t border-border px-5 py-3 text-xs text-muted-foreground">
              Ordenados por lo que pide atención primero. Tocá un producto para ver por qué tiene
              ese precio.
            </p>
            {/* La paginación trae su propia raya arriba: acá sólo se alinea con la tarjeta. */}
            <div className="[&>div]:px-5">
              <Pagination
                page={pagina}
                totalPages={Math.max(1, Math.ceil(foto.total / POR_PAGINA))}
                totalItems={foto.total}
                pageSize={POR_PAGINA}
                onPageChange={setPagina}
                itemName="productos"
              />
            </div>
          </>
        )}
      </TarjetaDeCompite>
    </div>
  );
}

function FilaDeProducto({
  f,
  abierta,
  onTocar,
  alcancePorRegla,
  onVerHistorial,
}: {
  f: FilaDeLaFoto;
  abierta: boolean;
  onTocar: () => void;
  alcancePorRegla: Record<string, string>;
  /** El historial de precios (destino Productos, plan 3). Ausente = no se ofrece. */
  onVerHistorial?: ComoEstaCadaProductoProps['onVerHistorial'];
}) {
  const sinPedido = f.estado === 'SIN_ESTRATEGIA' || f.estado === 'SIN_DATOS';
  const margen = textoDeMargenPropio(f);
  const hayLimites = f.rangos.some((r) => r.aviso == null && (r.min != null || r.max != null));
  const sirveOtro =
    !sinPedido &&
    f.estado !== 'SIN_PRECIO_POSIBLE' &&
    Math.abs(f.precioFinal - f.precioPropuesto) > 0.005;
  const limitantes = nombresDeLimitantes(f);
  // Sin costo y dentro del rango: lo que pide atención es el costo que falta.
  const sinCostoLibre = f.sinCosto && f.estado === 'LIBRE';

  return (
    <Fragment>
      <TableRow
        className={cn(
          'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
          abierta && 'bg-muted/40 hover:bg-muted/40'
        )}
        onClick={onTocar}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onTocar();
          } else if (e.key === ' ') {
            e.preventDefault();
            onTocar();
          }
        }}
        tabIndex={0}
        aria-expanded={abierta}
      >
        <TableCell className="py-3 pl-5 align-top">
          <CeldaProducto
            nombre={
              <>
                {f.name} {/* Se abre y se cierra: la flecha gira (antes ▸ / ▾). */}
                <ChevronRight
                  aria-hidden="true"
                  className={cn(
                    'inline h-3.5 w-3.5 align-[-2px] text-muted-foreground transition-transform',
                    abierta && 'rotate-90'
                  )}
                />
              </>
            }
            detalle={`${f.estrategia?.name ?? 'sin estrategia'} · hoy ${fmt(f.precioHoy)}`}
          />
        </TableCell>
        <TableCell className="py-3 text-right align-top tabular-nums">
          <span className="tabular-nums">{sinPedido ? '—' : fmt(f.precioPedido)}</span>
          {margen && <div className="text-xs text-muted-foreground">{margen.debajoDelPedido}</div>}
        </TableCell>
        <TableCell className="py-3 align-top">
          <div className="tabular-nums text-xs">{textoCruce(f.cruce, hayLimites)}</div>
          <div className="mt-1.5 w-32">
            <BarraDeRangos
              rangos={f.rangos}
              cruce={f.cruce}
              marcas={{ queda: sinPedido ? null : f.precioFinal }}
              compacta
            />
          </div>
        </TableCell>
        <TableCell className="py-3 text-right align-top font-semibold tabular-nums text-foreground">
          <span className="tabular-nums">
            {f.estado === 'SIN_PRECIO_POSIBLE'
              ? 'a elegir'
              : sinPedido
                ? '—'
                : fmt(f.precioPropuesto)}
          </span>
          {sirveOtro && (
            <div className="text-xs font-normal text-muted-foreground">
              mientras nadie decide se sirve {fmt(f.precioFinal)}
            </div>
          )}
        </TableCell>
        <TableCell className="py-3 pr-5 align-top">
          <Etiqueta tono={sinCostoLibre ? 'alerta' : TONO_DEL_ESTADO[f.estado]}>
            {sinCostoLibre ? 'Sin costo' : conMayuscula(TEXTO_ESTADO[f.estado])}
          </Etiqueta>
          <div className="mt-1 text-xs text-muted-foreground">
            {/* La causa que manda el backend (`avisos[0]`), no una fija. Las partes van
                separadas con « · »: pegadas se leían como una sola frase (ronda final, C9). */}
            {[
              f.estado === 'LIMITADO' ? limitantes.join(' · ') : null,
              f.estado === 'SIN_DATOS' ? porQueDeLaFila(f) : null,
              f.estado === 'LIBRE' ? margen?.enElEstado : null,
              // En LIBRE sin costo la etiqueta ya dice «Sin costo»: no se repite.
              f.sinCosto
                ? sinCostoLibre
                  ? 'El margen no lo cuida'
                  : 'Sin costo: el margen no lo cuida'
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </TableCell>
      </TableRow>
      {abierta && (
        <TableRow className="bg-muted/40 hover:bg-muted/40">
          {/* A todo el ancho: la frase del porqué ya no se corta en una columna angosta. */}
          <TableCell colSpan={5} className="px-5 pb-5 pt-1">
            {/* El mismo desglose que la simulación, sin regla nueva: quién decide
                (la estrategia, una regla o varias), qué pide cada una, si se pueden cumplir todas. */}
            <DesgloseSimulado fila={f} costo={f.costo} alcancePorRegla={alcancePorRegla} />
            <div className="mt-3 flex flex-wrap gap-5 text-sm">
              {onVerHistorial && (
                <Button
                  variant="link"
                  className="h-auto p-0 text-sm"
                  onClick={() => onVerHistorial({ productId: f.productId, name: f.name })}
                >
                  Ver su historial de precios
                </Button>
              )}
              {f.estado === 'SIN_PRECIO_POSIBLE' && (
                <a
                  href={hrefDeLaEmpresa(hrefsDeCompite.revisar())}
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  {/* Compite (rediseño del 14-oct): el lugar ahora se llama Revisar precios. */}
                  Resolver en Revisar precios
                </a>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}
